/* eslint-disable no-nested-ternary */
import { useLazyQuery } from '@apollo/client';
import {
    Add,
    AttachFileOutlined,
    Bolt,
    CheckBoxOutlineBlankOutlined,
    CheckBoxOutlined,
    CloseOutlined,
    Event,
    KeyboardArrowDown
} from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormGroup,
    Grid,
    IconButton,
    InputAdornment,
    styled,
    Switch,
    TextField,
    TextFieldProps,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import { DatePicker, DateTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { QUERY_GET_MY_RECORDS_FILE_BY_ID } from 'graphql/queries/records';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, UseFormClearErrors, UseFormSetValue } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { CustomMultiSelect } from 'ui-component/formComponents/CustomMultiSelect';
import { LazyLoadingList } from 'ui-component/formComponents/LazyLoadingList/';
import { Entity } from 'ui-component/icons';
import { IRecordField, IRecordFieldWithFieldName } from 'ui-component/records/types';
import { DROPDOWN_COLUMNS, DROPDOWN_COLUMNS_SINGLE } from 'ui-component/records/utils/columnHelpers';
import { isJsonString } from 'utils/stringHelpers';
import { isValidEmail } from 'utils/stringHelpers/isValidEmail';
import { PanelFieldSectionInline } from 'views/CreateRecord/components';
import { DynamicObject } from 'views/TenantProfileSettings/components/types';
import { ListValue } from 'views/backoffice/CustomLists/types';
import { ObjectProperty } from 'views/backoffice/CustomObjects/types';
import { AttachmentRow } from '../Attachments/types';
import { IGetRecordFileById } from '../types';
import { getMultiselectListLabel, isValidJson, normalizeBooleanValue } from '../utils';
import FileCard from './FileCard';
import InputMask from './InputMaskPhone';
import InlineFileListDisplay from 'ui-component/records/RecordGrid/components/InlineFileListDisplay';

export interface renderInputProps {
    fieldData: IRecordFieldWithFieldName;
    resetField: any;
    name: string;
    control: any;
    errors: any;
    options?: any[];
    isCustomList?: boolean;
    disabled?: boolean;
    isRequired?: boolean;
    setValue?: UseFormSetValue<any>;
    handleOpenViewObjectPanel?: (
        field: IRecordField,
        fieldName: string,
        editable: boolean,
        selectedObject?: Pick<DynamicObject, 'id' | 'objectValues' | 'objectDefinition'> | null
    ) => void;
    inMemoryObjectProperties?: { [key: string]: (ObjectProperty & { value: string })[] };
    TextFieldComponent?: (props: TextFieldProps) => JSX.Element | null;
    TextFieldComponentProps?: Omit<TextFieldProps, 'ref' | 'defaultValue' | 'type'>;
    ignoreDrawer?: boolean;
    label?: string;
    backgroundAlways?: boolean;
    defaultValue?: string;
    isObjectProperty?: boolean;
    activeDataFocusView?: boolean;
    hideError?: boolean;
    loadingListValues?: boolean;
    onOpenDropdown?: (manualTriggered?: boolean) => void;
    width?: number;
    clearErrors?: UseFormClearErrors<any>;
    isDraggable?: boolean;
    isDragging?: boolean;
    showFieldMenu?: boolean;
    rightHandPanelWidth?: number;
    isRecordViewer?: boolean;
    objectName?: string;
    recordId?: string;
    recordTypeId?: string;
    hideTitle?: boolean;
}

export const BaseTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-root': {
        width: '100%',
        height: '100%',
        backgroundColor: theme.palette.primary[300],
        '&.Mui-focused': {
            backgroundColor: `${theme.palette.secondary.light} !important`
        },
        '&.Mui-disabled': {
            backgroundColor: `${theme.palette.primary[500]} !important`
        },
        '& .MuiInputBase-input': {
            padding: '0px !important',
            margin: '0px !important',
            width: '100%',
            height: '100%',
            backgroundColor: theme.palette.primary[300],
            '&.Mui-focused': {
                backgroundColor: `${theme.palette.secondary.light} !important`
            },
            '&.Mui-disabled': {
                backgroundColor: `${theme.palette.primary[500]} !important`
            }
        }
    },

    '& fieldset': {
        border: 'none !important'
    },

    '& :focus-visible, :focus, :active, .MuiInputBase-root .Mui-focused': {
        backgroundColor: `${theme.palette.secondary.light} !important`,
        color: `${theme.palette.secondary.main} !important`,
        '&::placeholder': { color: 'transparent !important' },
        '& svg': {
            color: `${theme.palette.secondary.main} !important`,
            opacity: 0.5
        }
    }
}));

const hardCodeRecordFieldId = 'recordFileId';

export const RenderInput = ({
    fieldData,
    name,
    control,
    errors,
    options = [],
    disabled,
    isRequired,
    resetField,
    setValue,
    handleOpenViewObjectPanel,
    inMemoryObjectProperties,
    TextFieldComponent = BaseTextField as (props: TextFieldProps) => JSX.Element,
    TextFieldComponentProps = {},
    label,
    defaultValue = '',
    isObjectProperty,
    loadingListValues,
    onOpenDropdown,
    clearErrors,
    isDraggable,
    isDragging,
    showFieldMenu = true,
    rightHandPanelWidth,
    isRecordViewer,
    hideError,
    objectName,
    recordId,
    recordTypeId,
    hideTitle = false
}: renderInputProps) => {
    const themeSys = useTheme();
    const textFieldRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    // Función para detectar si el texto se desborda
    const checkTextOverflow = (element: HTMLDivElement | null, text: string) => {
        if (!element || !text) return false;

        const inputElement = element.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
        if (!inputElement) return false;

        // Crear un elemento temporal para medir el texto
        const tempElement = document.createElement('div');
        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.whiteSpace = 'nowrap';
        tempElement.style.font = window.getComputedStyle(inputElement).font;
        tempElement.textContent = text;

        document.body.appendChild(tempElement);
        const textWidth = tempElement.offsetWidth;
        document.body.removeChild(tempElement);

        const inputWidth = inputElement.offsetWidth;
        return textWidth > inputWidth;
    };

    // Efecto para verificar overflow después de que el componente se monte
    useEffect(() => {
        if (disabled && textFieldRef.current) {
            const inputElement = textFieldRef.current.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
            if (inputElement && inputElement.value) {
                const isOverflowing = checkTextOverflow(textFieldRef.current, inputElement.value);
                setShowTooltip(isOverflowing);
            }
        }
    }, [disabled]);

    const [inputFocused, setInputFocused] = useState(false);
    const docRef = React.useRef<HTMLInputElement | null>(null);
    const [localErrorMessage, setLocalErrorMessage] = useState<string>('');
    const dispatch = useDispatch();
    const [inputFile, setInputFile] = useState<File | null>(null);
    const [fileKey, setFileKey] = useState('');
    const [chargingFile, setChargingFile] = useState(false);
    const [isNewFile, setIsNewFile] = useState(false);
    const [description, setDescription] = useState('');
    const [fileRecordId, setFileRecordId] = useState(null);
    const isRecordField = fieldData.fieldName === hardCodeRecordFieldId;

    const getErrorObj = () => {
        if (!isObjectProperty && (!fieldData.gridEditable || disabled)) return null;
        return errors[name] || errors[label as keyof typeof errors];
    };

    const errorObj = getErrorObj();
    const error = errorObj?.message || localErrorMessage;

    const [getRecordFile, { loading: docLoading, data: docData }] = useLazyQuery<IGetRecordFileById>(QUERY_GET_MY_RECORDS_FILE_BY_ID, {
        fetchPolicy: 'no-cache'
    });

    const mapObjectPropertiesToValue = (properties: (ObjectProperty & { value: string })[] | undefined) => {
        const displayValue: string[] = [];
        if (properties !== undefined) {
            properties
                .sort((a, b) => {
                    const orderA = a.order;
                    const orderB = b.order;
                    if (orderA < orderB) {
                        return -1;
                    }
                    if (orderA > orderB) {
                        return 1;
                    }
                    return 0;
                })
                .forEach((val) => {
                    if (val.isDisplayable) {
                        displayValue.push(val.value);
                    }
                });
            return displayValue.join(', ');
        }
        return '';
    };

    const fetchRecordFile = useCallback(() => {
        if (fileRecordId) {
            getRecordFile({
                variables: {
                    recordFileId: Number(fileRecordId)
                }
            });
        }
    }, [fileRecordId, getRecordFile]);

    useEffect(() => {
        if (docData?.getMyRecordFileById && !docLoading && fileRecordId) {
            setFileKey(docData.getMyRecordFileById.originalFileName);
        }
    }, [docData?.getMyRecordFileById, docLoading, fileRecordId]);

    useEffect(() => {
        fetchRecordFile();
    }, [fetchRecordFile]);

    const handleFileUpload = async (e: any) => {
        const MAX_FILE_SIZE_MB = 10;
        const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024 - 1024; // Slightly less than 10 MB
        const file = e.target.files[0];

        if (file.size > MAX_FILE_SIZE_BYTES) {
            dispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Error',
                    subtitle: `The file size exceeds the maximum limit of ${MAX_FILE_SIZE_MB} MB. Please upload a smaller file.`
                })
            );
            return;
        }

        setChargingFile(true);
        setInputFile(file);
        setValue?.(name, file);
        setChargingFile(false);
        setIsNewFile(true);
        setFileKey(URL.createObjectURL(file));
    };

    const handleClearInput = (inputRef?: HTMLInputElement) => {
        if (inputRef) {
            inputRef.value = '';
        }
        setFileKey('');
        setInputFile(null);
        setDescription('');
        setValue?.(name, '');
        resetField?.(name, '');
    };

    const onchangeAction = (event: any, field: any) => {
        clearError();
        if (fieldData.dataType === 'email') {
            const newValue = event?.target.value;
            if (newValue === null) {
                field.onChange('');
                setLocalErrorMessage('');
            } else {
                const validEmail = isValidEmail(newValue);
                if (validEmail) {
                    setLocalErrorMessage('');
                } else {
                    setLocalErrorMessage('Invalid format. Please enter a valid email address (e.g. example@domain.com).');
                }
                field.onChange(newValue);
            }
        } else if (fieldData.dataType === 'url') {
            const newValue = event?.target.value;
            const urlRegex = /^(https?:\/\/)?([\w]+\.)+[\w]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;
            if (newValue === null) {
                field.onChange('');
                setLocalErrorMessage('');
            } else {
                const validUrl = urlRegex.test(newValue);
                if (validUrl) {
                    setLocalErrorMessage('');
                } else {
                    setLocalErrorMessage('Invalid format. Please enter a valid URL (e.g. https://example.com).');
                }
                field.onChange(newValue);
            }
        } else if (fieldData.dataType === 'phone') {
            const newValue = event?.target?.value;
            if (!newValue) {
                field.onChange('');
                setLocalErrorMessage('');
                return;
            }

            const complement = JSON.parse(newValue)[1] || '';

            if (complement.toString().length < 9) {
                setLocalErrorMessage('Please enter a complete phone number');
            } else {
                setLocalErrorMessage('');
            }

            field.onChange(newValue);
        } else if (fieldData.dataType === 'datetime' || fieldData.dataType === 'time') {
            const newValue = event?.target?.value || event;
            const date = dayjs(newValue);
            if (!newValue || !date.isValid()) {
                field.onChange('');
                setLocalErrorMessage('Invalid format. Please enter a valid date/time.');
            } else {
                field.onChange(date.toISOString());
                setLocalErrorMessage('');
            }
        } else {
            field.onChange(event);
            if (event?.target?.value && event.target.value.trim() !== '') {
                setLocalErrorMessage('');
            }
        }
    };

    const getDefaultValueAttachment = () => {
        const storedDefaultAttachment = localStorage.getItem('defaultValueAttachment');
        if (storedDefaultAttachment) {
            return storedDefaultAttachment;
        }
        return '';
    };

    const clearError = () => {
        if (!isRecordViewer) return;
        if (name && typeof name === 'string') clearErrors?.(name);
        if (label as keyof typeof errors) clearErrors?.(label);
    };

    const requiredPlaceholder = isRequired && error ? 'Required field' : '';

    const renderTextField = (field: any) => {
        const title = label || name;
        const isNumber = fieldData.dataType === 'number';
        const booleanData = typeof field.value === 'boolean';

        if (title === 'businessEntity') {
            const entityName = options?.find((op) => op?.id === +field?.value)?.name || '';
            return (
                <TextFieldComponent
                    {...field}
                    value={entityName}
                    disabled
                    fullWidth
                    sx={{
                        '& .MuiInputBase-input': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }
                    }}
                    InputProps={{
                        ...(entityName && {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Entity sx={{ width: '16px', height: '16px' }} />
                                </InputAdornment>
                            )
                        })
                    }}
                />
            );
        }

        if ((fieldData.fieldName !== hardCodeRecordFieldId && fieldData.dataType === 'currency') || (isNumber && !booleanData)) {
            return (
                <NumberFormat
                    customInput={TextFieldComponent}
                    name={title}
                    sx={{ '& fieldset': { border: errorObj ? 'solid !important' : 'none' } }}
                    fixedDecimalScale={fieldData.dataType === 'currency'}
                    thousandSeparator={fieldData.dataType === 'currency' ? ',' : ''}
                    decimalScale={fieldData.dataType === 'currency' ? 2 : 0}
                    variant="outlined"
                    fullWidth
                    value={field.value ?? ''}
                    onValueChange={(values) => {
                        const value = values.floatValue;
                        if (value === undefined) field.onChange('');
                        else field.onChange(value);
                        clearError();
                    }}
                    disabled={disabled}
                    {...TextFieldComponentProps}
                    onFocus={() => {
                        setInputFocused(true);
                    }}
                    onBlur={() => {
                        setInputFocused(false);
                    }}
                    placeholder={requiredPlaceholder}
                    allowNegative
                />
            );
        }

        if (fieldData.dataType === 'boolean' || (isNumber && booleanData)) {
            const normalizedValue = normalizeBooleanValue(field.value);
            const checked = normalizedValue;

            return (
                <Switch
                    checked={checked}
                    onChange={(event) => {
                        setValue?.(name, event.target.checked, { shouldValidate: true });
                        clearError();
                    }}
                    sx={(theme) => ({
                        '& .MuiSwitch-thumb': {
                            backgroundColor: disabled || !checked ? theme.palette.background.paper : theme.palette.primary.dark
                        },
                        '& .MuiSwitch-track': {
                            backgroundColor:
                                disabled || !checked ? theme.palette.primary[400] : `${theme.palette.secondary[800]} !important`
                        }
                    })}
                    disabled={disabled}
                />
            );
        }

        if (fieldData.dataType === 'checkbox') {
            const normalizedValue = normalizeBooleanValue(field.value);
            const checked = normalizedValue;

            return (
                <Checkbox
                    name={title}
                    checked={checked}
                    disabled={disabled}
                    onChange={(event) => {
                        // Enviar boolean real, no string
                        setValue?.(name, event.target.checked, { shouldValidate: true });
                        clearError();
                    }}
                    sx={{
                        padding: 0,
                        color: themeSys.palette.primary[400],
                        '&.Mui-checked': { color: themeSys.palette.primary.dark },
                        '& .MuiSvgIcon-root': { fontSize: '1.25rem' }
                    }}
                    icon={<CheckBoxOutlineBlankOutlined />}
                    checkedIcon={<CheckBoxOutlined />}
                />
            );
        }

        if (fieldData.dataType === 'phone') {
            return (
                <InputMask
                    sx={{
                        '& input': {
                            background: `${themeSys.palette.primary[300]} !important`
                        },
                        '& fieldset': {
                            border: 'none !important'
                        }
                    }}
                    {...field}
                    value={field.value ?? ''}
                    variant="outlined"
                    fullWidth
                    onFocus={() => {
                        setInputFocused(true);
                    }}
                    onBlur={() => {
                        setInputFocused(false);
                    }}
                    onChange={(event) => {
                        onchangeAction(event, field);
                    }}
                    disabled={disabled}
                    {...TextFieldComponentProps}
                    autoWidth
                    placeholder={requiredPlaceholder}
                />
            );
        }

        if (fieldData.dataType === 'object') {
            const objectValue = mapObjectPropertiesToValue(inMemoryObjectProperties?.[name]) || field.value;
            const isObjectEmpty = !objectValue;
            const objectValueFormatted = objectValue?.replace('true', 'Yes').replace('false', 'No');

            return (
                <Box width="100%" display="flex" alignItems="center" justifyContent="space-between">
                    <Box
                        width="90%"
                        onClick={() => {
                            handleOpenViewObjectPanel?.(fieldData, name, !disabled);
                        }}
                    >
                        {isObjectEmpty ? (
                            <Typography
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    color: '#C6C4C4'
                                }}
                            >
                                Add Data
                            </Typography>
                        ) : (
                            <Box width="100%" display="flex" alignItems="center" justifyContent="space-between">
                                <Typography
                                    title={objectValueFormatted}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        color: themeSys.palette.primary.dark,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 500
                                    }}
                                >
                                    {objectValueFormatted}
                                </Typography>
                                {!disabled && (
                                    <IconButton
                                        onClick={() => {
                                            handleOpenViewObjectPanel?.(fieldData, name, !disabled);
                                        }}
                                    >
                                        <CloseOutlined sx={{ fontSize: '1rem', color: themeSys.palette.primary[600] }} />
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    </Box>
                    <IconButton
                        sx={{ m: 0, p: 0 }}
                        onClick={() => {
                            handleOpenViewObjectPanel?.(fieldData, name, !disabled);
                        }}
                    >
                        <KeyboardArrowDown sx={{ fontSize: '1rem', color: themeSys.palette.primary[600] }} />
                    </IconButton>
                </Box>
            );
        }

        if (fieldData.dataType === 'attachment' || isRecordField) {
            // Parse files from field value
            let parsedFiles: AttachmentRow[] = [];
            try {
                if (field.value && isValidJson(field.value)) {
                    const parsedValue = JSON.parse(field.value);
                    if (Array.isArray(parsedValue)) {
                        parsedFiles = parsedValue;
                    } else if (parsedValue && typeof parsedValue === 'object' && 'value' in parsedValue) {
                        // Handle case where value is wrapped in an object
                        const unwrapped = JSON.parse(parsedValue.value);
                        parsedFiles = Array.isArray(unwrapped) ? unwrapped : [];
                    }
                }
            } catch (e) {
                console.error('Error parsing attachment JSON:', e);
            }

            // Use InlineFileListDisplay if recordId and recordTypeId are provided
            if (recordId && recordTypeId && fieldData.id) {
                return (
                    <InlineFileListDisplay
                        files={parsedFiles}
                        recordId={recordId}
                        recordTypeId={recordTypeId}
                        additionalFieldId={Number(fieldData.id)}
                    />
                );
            }
            // Fallback to original implementation if required props are missing
            let defaultValueForAttachment: string | null = '';
            const initValue = (() => {
                try {
                    const parsedValue = defaultValue ? JSON.parse(defaultValue) : '';
                    return parsedValue && typeof parsedValue === 'object' && 'value' in parsedValue ? parsedValue.value : defaultValue;
                } catch (e) {
                    return defaultValue;
                }
            })();

            if (!getDefaultValueAttachment() || (getDefaultValueAttachment() && getDefaultValueAttachment() !== initValue)) {
                localStorage.setItem('defaultValueAttachment', initValue);
            }

            if (localStorage.getItem('defaultValueAttachment')) {
                defaultValueForAttachment = getDefaultValueAttachment();
            }

            if (!isNewFile) {
                try {
                    if (isValidJson(field.value)) {
                        const attachment = JSON.parse(field.value) as AttachmentRow[];
                        if (attachment[0]?.name) {
                            setFileKey(attachment[0]?.name);
                            setValue?.(name, attachment);
                            setDescription?.(attachment[0]?.description || '');
                        }
                    } else if (field.value instanceof File) {
                        setFileKey(field.value.name);
                        setDescription?.(field.value.name);
                        setInputFile(field.value);
                    }
                } catch (e) {
                    console.error('Error parsing attachment JSON:', e);
                }
            }

            if (fieldData.fieldName === hardCodeRecordFieldId && !!field.value) {
                setFileRecordId(field.value);
            }

            return (
                <>
                    <input
                        type="file"
                        ref={docRef}
                        onChange={(e) => {
                            handleFileUpload(e);
                            clearError();
                        }}
                        style={{ display: 'none' }}
                        accept={fieldData.fieldName === hardCodeRecordFieldId ? 'application/pdf' : undefined}
                    />
                    {!fileKey && (
                        <Button
                            onClick={() => (docRef.current ? docRef.current.click() : null)}
                            fullWidth
                            sx={{
                                height: '100%',
                                width: '100%',
                                borderRadius: '0.5rem',
                                p: '0.25rem',
                                backgroundColor: disabled ? themeSys.palette.primary[500] : themeSys.palette.background.paper,
                                borderColor: error || disabled ? 'transparent !important' : themeSys.palette.primary.dark,
                                '&:hover': {
                                    borderColor: error ? 'transparent' : themeSys.palette.primary.dark
                                }
                            }}
                            variant="outlined"
                            disabled={disabled}
                        >
                            <Add
                                sx={{
                                    fontSize: '1.25rem',
                                    color: disabled ? '#C6C4C4' : themeSys.palette.primary.dark,
                                    mr: '0.5rem'
                                }}
                            />
                            <Typography fontWeight={500} fontSize="0.875rem" color={disabled ? '#C6C4C4' : themeSys.palette.primary.dark}>
                                Add Attachments
                            </Typography>
                            {chargingFile && <CircularProgress size={20} color="inherit" />}
                        </Button>
                    )}

                    {(fileKey || inputFile) && (
                        <FileCard
                            fileKeyS3={fileKey}
                            file={inputFile}
                            onRemove={() => (isRecordField ? handleClearInput(docRef.current!) : handleClearInput())}
                            description={
                                fieldData.fieldName === hardCodeRecordFieldId
                                    ? ''
                                    : description || defaultValueForAttachment || fieldData.description || ''
                            }
                            isNewFile={isNewFile}
                            onChangeDescription={(newDescription: string) => {
                                setValue?.(`tmp_${name}_description`, newDescription);
                                setDescription?.(newDescription);
                            }}
                            doNotShowDescription
                            isRecordField={isRecordField}
                        />
                    )}
                </>
            );
        }

        if (fieldData.dataType === 'multiselect') {
            const newOptions = options;
            const optionMultiSelect = newOptions
                .sort((a, b) => a.order - b.order)
                .map((option: any) => ({
                    id: +option.id,
                    label: fieldData.listType?.dataType && getMultiselectListLabel(fieldData.listType?.dataType, option),
                    enabled: option.enabled
                }));

            let parsedValue: number[] = [];
            try {
                if (field.value && field.value !== '0') {
                    parsedValue = JSON.parse(field.value);
                }
            } catch (errorParsing) {
                console.error('Error parsing field value:', field.value, errorParsing);
                parsedValue = [];
            }

            return (
                <CustomMultiSelect
                    disabled={disabled}
                    loading={loadingListValues}
                    options={optionMultiSelect}
                    error={!!error}
                    value={parsedValue}
                    onChange={(value: number[]) => {
                        const stringValue = value.length === 0 ? '' : JSON.stringify(value);
                        setValue?.(name, stringValue, { shouldValidate: true });
                        clearError();
                    }}
                    onOpen={() => {
                        onOpenDropdown?.(true);
                        setInputFocused(true);
                    }}
                    onClose={() => {
                        setInputFocused(false);
                    }}
                    sx={{
                        justifyContent: 'space-between'
                    }}
                />
            );
        }

        if (fieldData.dataType === 'multiselect-checkbox') {
            return (
                <FormGroup row sx={{ flexWrap: 'wrap', overflow: 'scroll', maxWidth: '100%' }}>
                    {options
                        .filter((el) => el.enabled)
                        .map((option, index) => (
                            <FormGroup key={index} sx={{ flexWrap: 'nowrap', width: '100%', gap: '1rem' }}>
                                <Grid container alignItems="center" flexWrap="nowrap">
                                    <Grid item xs={8}>
                                        <Typography
                                            style={{
                                                fontWeight: 400,
                                                fontSize: '0.875rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {option.userValue?.name || option.value}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3} alignItems="center">
                                        <Checkbox
                                            name={title}
                                            checked={
                                                !!field.value &&
                                                isJsonString(field.value) &&
                                                Array.isArray(JSON.parse(field.value)) &&
                                                JSON.parse(field.value).some((x: string | number) => String(x) === String(option.id))
                                            }
                                            disabled={disabled}
                                            onChange={(event) => {
                                                let currentValue = isJsonString(field.value) ? JSON.parse(field.value || '[]') : [];
                                                if (!Array.isArray(currentValue)) {
                                                    currentValue = [];
                                                }
                                                if (event.target.checked) {
                                                    currentValue.push(+option.id);
                                                } else {
                                                    currentValue.splice(currentValue.indexOf(+option.id), 1);
                                                }
                                                setValue?.(name, currentValue.length ? JSON.stringify(currentValue) : '', {
                                                    shouldValidate: true,
                                                    shouldDirty: true
                                                });
                                                clearError();
                                            }}
                                            sx={{
                                                padding: 0,
                                                color: themeSys.palette.primary[400],
                                                '&.Mui-checked': { color: themeSys.palette.primary.dark },
                                                '& .MuiSvgIcon-root': { fontSize: '1.25rem' }
                                            }}
                                            icon={<CheckBoxOutlineBlankOutlined />}
                                            checkedIcon={<CheckBoxOutlined />}
                                        />
                                    </Grid>
                                </Grid>
                            </FormGroup>
                        ))}
                </FormGroup>
            );
        }

        if (
            ['single-select', 'dropdown'].includes(fieldData.dataType.toLowerCase()) ||
            DROPDOWN_COLUMNS_SINGLE.includes(name as (typeof DROPDOWN_COLUMNS_SINGLE)[number]) ||
            DROPDOWN_COLUMNS.includes(name as (typeof DROPDOWN_COLUMNS)[number])
        ) {
            return (
                <LazyLoadingList
                    listId={fieldData.listType?.id ? +fieldData.listType?.id : -1}
                    listDataType={fieldData.listType?.dataType}
                    selectedId={String(field.value)}
                    onChange={(value) => {
                        field.onChange(value);
                        clearError();
                    }}
                    error={!!error}
                    disabled={disabled}
                    handleClickSelectedOption={() => {
                        const selectedObjectListValue: ListValue = options.find((el) => +el.id === +field.value);
                        handleOpenViewObjectPanel?.(fieldData, name, false, selectedObjectListValue.dynamicObjectValue);
                    }}
                    onOpen={() => {
                        setInputFocused(true);
                    }}
                    onClose={() => {
                        setInputFocused(false);
                    }}
                    options={options}
                    loading={loadingListValues}
                />
            );
        }

        if (fieldData.dataType === 'date') {
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        onChange={(value) => {
                            const date = dayjs(value);
                            const isValidDate = date.isValid();
                            if (isValidDate) {
                                field.onChange(date.toISOString());
                                clearErrors?.(name);
                            } else if (value === null) {
                                field.onChange('');
                                clearErrors?.(name);
                            } else {
                                setLocalErrorMessage('Invalid date format');
                            }
                        }}
                        inputRef={field.ref}
                        value={field.value ? dayjs(field.value) : null}
                        disabled={disabled}
                        renderInput={(params) => (
                            <TextFieldComponent
                                name={label || name}
                                onFocus={() => {
                                    setInputFocused(true);
                                }}
                                onBlur={() => {
                                    setInputFocused(false);
                                }}
                                fullWidth
                                {...params}
                                InputProps={{
                                    ...TextFieldComponentProps.InputProps,
                                    ...params.InputProps
                                }}
                                variant={TextFieldComponentProps.variant || params.variant}
                                size={TextFieldComponentProps.size || params.size}
                                placeholder={requiredPlaceholder}
                            />
                        )}
                    />
                </LocalizationProvider>
            );
        }

        if (fieldData.dataType === 'datetime') {
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ todayButtonLabel: 'NOW' }}>
                    <DateTimePicker
                        onChange={(value) => {
                            const date = dayjs(value);
                            const isValidDate = date.isValid();
                            if (isValidDate) {
                                field.onChange(date.toISOString());
                                clearErrors?.(name);
                            } else if (value === null) {
                                field.onChange('');
                                clearErrors?.(name);
                            } else {
                                setLocalErrorMessage('Invalid date format');
                            }
                        }}
                        value={field.value ? dayjs(field.value) : null}
                        inputRef={field.ref}
                        disabled={disabled}
                        components={{
                            OpenPickerIcon: () => (
                                <Tooltip title="Pick a date">
                                    <Event />
                                </Tooltip>
                            )
                        }}
                        componentsProps={{
                            actionBar: {
                                actions: ['today']
                            }
                        }}
                        renderInput={(params) => (
                            <TextFieldComponent
                                name={label || name}
                                onFocus={() => {
                                    setInputFocused(true);
                                }}
                                onBlur={() => {
                                    setInputFocused(false);
                                }}
                                {...params}
                                {...TextFieldComponentProps}
                                InputProps={{
                                    ...TextFieldComponentProps.InputProps,
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            <Tooltip title="Now">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            field.onChange(dayjs().toISOString());
                                                            clearError();
                                                        }}
                                                        disabled={disabled}
                                                        sx={{ transform: 'translateX(15px)', marginLeft: '-20px' }}
                                                    >
                                                        <Bolt />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {params.InputProps?.endAdornment}
                                        </>
                                    )
                                }}
                                fullWidth
                                placeholder={requiredPlaceholder}
                            />
                        )}
                    />
                </LocalizationProvider>
            );
        }

        if (fieldData.dataType === 'time') {
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                        onChange={(value) => {
                            const date = dayjs(value);
                            const isValidDate = date.isValid();
                            if (isValidDate) {
                                field.onChange(date.toISOString());
                                clearError();
                            } else {
                                setLocalErrorMessage('Invalid date format');
                            }
                        }}
                        value={field.value ? dayjs(field.value) : null}
                        inputRef={field.ref}
                        disabled={disabled}
                        renderInput={(params) => (
                            <TextFieldComponent
                                name={label || name}
                                onFocus={() => {
                                    setInputFocused(true);
                                }}
                                onBlur={() => {
                                    setInputFocused(false);
                                }}
                                fullWidth
                                {...params}
                                {...TextFieldComponentProps}
                                placeholder={requiredPlaceholder}
                            />
                        )}
                    />
                </LocalizationProvider>
            );
        }

        const textField = (
            <>
                <TextFieldComponent
                    fullWidth
                    {...field}
                    onFocus={() => {
                        setInputFocused(true);
                    }}
                    onBlur={() => {
                        setInputFocused(false);
                    }}
                    disabled={disabled}
                    {...TextFieldComponentProps}
                    onChange={(event) => {
                        onchangeAction(event, field);
                    }}
                    placeholder={requiredPlaceholder}
                    multiline={fieldData.dataType === 'textarea'}
                    variant="outlined"
                    error={!!errorObj}
                    {...TextFieldComponentProps}
                />
                {fieldData.dataType === 'url' && (
                    <AttachFileOutlined
                        sx={{ fontSize: '1.25rem', color: themeSys.palette.primary[600], rotate: '30deg', cursor: 'pointer' }}
                    />
                )}
            </>
        );

        // Si el campo está deshabilitado y es un campo string, verificar si necesita tooltip
        if (disabled && field.value && typeof field.value === 'string' && field.value.trim() !== '') {
            if (showTooltip) {
                return (
                    <Tooltip title={field.value} placement="top" arrow>
                        <Box ref={textFieldRef} sx={{ width: '100%' }}>
                            {textField}
                        </Box>
                    </Tooltip>
                );
            }
            return (
                <Box ref={textFieldRef} sx={{ width: '100%' }}>
                    {textField}
                </Box>
            );
        }

        return textField;
    };

    const centerContent = ['checkbox', 'attachment', 'boolean'].includes(fieldData.dataType) || isRecordField;
    const title = label || name;
    const disabledField = disabled || title === 'businessEntity';

    return (
        <PanelFieldSectionInline
            title={title}
            isFocused={inputFocused}
            isRequired={isRequired}
            dataType={fieldData.dataType}
            disabled={disabledField}
            error={error || ''}
            isRecordField={isRecordField}
            centerContent={centerContent}
            isDraggable={isDraggable}
            isDragging={isDragging}
            showFieldMenu={showFieldMenu}
            rightHandPanelWidth={rightHandPanelWidth}
            isRecordViewer={isRecordViewer}
            objectName={objectName}
            fieldId={fieldData.id?.toString() || ''}
            hideTitle={hideTitle}
        >
            <Controller name={name} control={control} defaultValue={defaultValue} render={({ field }) => renderTextField(field)} />
        </PanelFieldSectionInline>
    );
};

export default RenderInput;
